import { encode, decode } from "@msgpack/msgpack";
import { SocketMessageInterface } from "../interfaces/Monitoring.interface";
import {
  directionStringToEnum,
  doorZoneStringToEnum,
  escControlStringToEnum,
} from "../flatbuffers/types/enumDisplayUtils";
import { uuidToString } from "./uuid";

// LN2R binary replay format (v2):
//   [4 bytes] Magic: "LN2R" (0x4C 0x4E 0x32 0x52)
//   [4 bytes] Version: uint32 LE (currently 2)
//   [4 bytes] Message count: uint32 LE
//   Per message:
//     [8 bytes] Timestamp: float64 LE (epoch milliseconds)
//     [4 bytes] Length: uint32 LE
//     [N bytes] MessagePack-encoded SocketMessageInterface

/**
 * One captured message with the epoch-millisecond timestamp it was seen at.
 */
export interface ReplayRecord {
  timestamp: number; // epoch milliseconds
  message: SocketMessageInterface;
}

const MAGIC = new Uint8Array([0x4c, 0x4e, 0x32, 0x52]); // "LN2R"
const VERSION = 2;
const HEADER_SIZE = 12; // 4 magic + 4 version + 4 count

/** Convert UuidT fields to strings so MessagePack can serialise them (BigInt is not supported). */
function toSerializable(msg: SocketMessageInterface): Record<string, unknown> {
  const { bankId, liftId, ...rest } = msg;
  return {
    ...rest,
    bankId: uuidToString(bankId),
    liftId: uuidToString(liftId),
  };
}

/**
 * Encodes replay records into a binary blob for download.
 */
export function encodeReplayFile(records: ReplayRecord[]): Blob {
  const encodedMessages = records.map((r) => encode(toSerializable(r.message)));

  let totalSize = HEADER_SIZE;
  encodedMessages.forEach((buf) => {
    totalSize += 8 + 4 + buf.byteLength; // 8 timestamp + 4 length + N data
  });

  const buffer = new ArrayBuffer(totalSize);
  const view = new DataView(buffer);
  const bytes = new Uint8Array(buffer);

  // Write header
  bytes.set(MAGIC, 0);
  view.setUint32(4, VERSION, true);
  view.setUint32(8, records.length, true);

  // Write messages
  let offset = HEADER_SIZE;
  records.forEach((record, i) => {
    view.setFloat64(offset, record.timestamp, true);
    offset += 8;
    const buf = encodedMessages[i];
    view.setUint32(offset, buf.byteLength, true);
    offset += 4;
    bytes.set(new Uint8Array(buf.buffer, buf.byteOffset, buf.byteLength), offset);
    offset += buf.byteLength;
  });

  return new Blob([buffer], { type: "application/octet-stream" });
}

/**
 * Decodes a replay file. Throws when the buffer is shorter than the header.
 */
export function decodeReplayFile(buffer: ArrayBuffer): ReplayRecord[] {
  if (buffer.byteLength < HEADER_SIZE) {
    throw new Error("File too small to be a valid LN2R replay file");
  }

  const view = new DataView(buffer);
  const bytes = new Uint8Array(buffer);

  // Validate magic
  for (let i = 0; i < 4; i += 1) {
    if (bytes[i] !== MAGIC[i]) {
      throw new Error("Invalid file format: missing LN2R magic header");
    }
  }

  // Validate version
  const version = view.getUint32(4, true);
  if (version !== VERSION) {
    throw new Error(`Unsupported LN2R version: ${version} (expected ${VERSION})`);
  }

  const count = view.getUint32(8, true);
  const records: ReplayRecord[] = [];
  let offset = HEADER_SIZE;

  for (let i = 0; i < count; i += 1) {
    if (offset + 12 > buffer.byteLength) {
      throw new Error(`Unexpected end of file at message ${i}`);
    }
    const timestamp = view.getFloat64(offset, true);
    offset += 8;
    const len = view.getUint32(offset, true);
    offset += 4;
    if (offset + len > buffer.byteLength) {
      throw new Error(`Unexpected end of file at message ${i} data`);
    }
    const msgBytes = bytes.slice(offset, offset + len);
    const raw = decode(msgBytes) as Record<string, unknown>;

    // Normalize old snake_case top-level field names from legacy replay files
    const snakeRenames: [string, string][] = [
      ["bank_id", "bankId"],
      ["lift_id", "liftId"],
      ["car_calls", "carCalls"],
      ["hall_calls", "hallCalls"],
      ["service_mode", "serviceMode"],
      ["active_faults", "activeFaults"],
      ["door_status", "doorStatus"],
      ["door_zone", "doorZone"],
      ["escalator_control", "escalatorControl"],
      ["position_map", "positionMap"],
      ["service_map", "serviceMap"],
    ];
    snakeRenames.forEach(([oldKey, newKey]) => {
      if (oldKey in raw && !(newKey in raw)) {
        raw[newKey] = raw[oldKey];
        delete raw[oldKey];
      }
    });

    // Normalize nested position-calls fields (car_calls, hall_calls children)
    const renamePositionCalls = (parent: Record<string, unknown>, key: string) => {
      const obj = parent[key];
      if (!obj || typeof obj !== "object") return;
      const o = obj as Record<string, unknown>;
      if ("front_lock" in o && !("frontLock" in o)) {
        o.frontLock = o.front_lock;
        delete o.front_lock;
      }
      if ("rear_lock" in o && !("rearLock" in o)) {
        o.rearLock = o.rear_lock;
        delete o.rear_lock;
      }
    };
    if (raw.carCalls && typeof raw.carCalls === "object") {
      renamePositionCalls(raw, "carCalls");
    }
    if (raw.hallCalls && typeof raw.hallCalls === "object") {
      const hc = raw.hallCalls as Record<string, unknown>;
      if ("up_assigned" in hc && !("upAssigned" in hc)) {
        hc.upAssigned = hc.up_assigned;
        delete hc.up_assigned;
      }
      if ("down_assigned" in hc && !("downAssigned" in hc)) {
        hc.downAssigned = hc.down_assigned;
        delete hc.down_assigned;
      }
      ["up", "down", "upAssigned", "downAssigned"].forEach((k) => {
        if (hc[k] && typeof hc[k] === "object") renamePositionCalls(hc, k);
      });
    }

    // Normalize nested door_status fields
    if (raw.doorStatus && typeof raw.doorStatus === "object") {
      const ds = raw.doorStatus as Record<string, unknown>;
      const dsRenames: [string, string][] = [
        ["front_open", "frontOpen"],
        ["rear_open", "rearOpen"],
        ["front_stalled", "frontStalled"],
        ["rear_stalled", "rearStalled"],
      ];
      dsRenames.forEach(([oldKey, newKey]) => {
        if (oldKey in ds && !(newKey in ds)) {
          ds[newKey] = ds[oldKey];
          delete ds[oldKey];
        }
      });
    }

    // Normalize old string-format replay files to enum values
    if (typeof raw.direction === "string") {
      raw.direction = directionStringToEnum(raw.direction);
    }
    if (typeof raw.doorZone === "string") {
      raw.doorZone = doorZoneStringToEnum(raw.doorZone);
    }
    // Support old snake_case field name from legacy replay files
    if (typeof (raw as any).door_zone === "string") {
      raw.doorZone = doorZoneStringToEnum((raw as any).door_zone);
      delete (raw as any).door_zone;
    }
    if (typeof raw.escalatorControl === "string") {
      raw.escalatorControl = escControlStringToEnum(raw.escalatorControl);
    }
    // Support old snake_case field name from legacy replay files
    if (typeof (raw as any).escalator_control === "string") {
      raw.escalatorControl = escControlStringToEnum((raw as any).escalator_control);
      delete (raw as any).escalator_control;
    }
    records.push({
      timestamp,
      message: raw as unknown as SocketMessageInterface,
    });
    offset += len;
  }

  return records;
}
